import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Toast, ToastType } from '@/types'

const DEFAULT_DURATION = 5000
const MAX_TOASTS = 5

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([])
  const timers = new Map<string, ReturnType<typeof setTimeout>>()

  const activeToasts = computed(() => toasts.value)

  function generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  function add(
    type: ToastType,
    message: string,
    options: { duration?: number; dismissible?: boolean } = {}
  ): string {
    const id = generateId()
    const duration = options.duration ?? DEFAULT_DURATION
    const dismissible = options.dismissible ?? true

    const toast: Toast = {
      id,
      type,
      message,
      duration,
      dismissible
    }

    // Add to queue, removing oldest if at max capacity
    if (toasts.value.length >= MAX_TOASTS) {
      const oldest = toasts.value[0]
      remove(oldest.id)
    }

    toasts.value.push(toast)

    // Set auto-dismiss timer if duration > 0
    if (duration > 0) {
      const timer = setTimeout(() => {
        remove(id)
      }, duration)
      timers.set(id, timer)
    }

    return id
  }

  function remove(id: string): void {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }

    const index = toasts.value.findIndex(t => t.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  function clear(): void {
    // Clear all timers
    for (const timer of timers.values()) {
      clearTimeout(timer)
    }
    timers.clear()
    toasts.value = []
  }

  // Convenience methods
  function success(message: string, options?: { duration?: number; dismissible?: boolean }): string {
    return add('success', message, options)
  }

  function error(message: string, options?: { duration?: number; dismissible?: boolean }): string {
    return add('error', message, { duration: 0, ...options }) // Errors persist by default
  }

  function info(message: string, options?: { duration?: number; dismissible?: boolean }): string {
    return add('info', message, options)
  }

  function warning(message: string, options?: { duration?: number; dismissible?: boolean }): string {
    return add('warning', message, options)
  }

  return {
    // State
    toasts: activeToasts,
    // Actions
    add,
    remove,
    clear,
    success,
    error,
    info,
    warning
  }
})
